import { kAnalyticsFeatureTables } from '@/analytics/constants'
import {
  TableCreationMethod,
  TableDocumentSource,
  TableDownloadScope,
  TableEditAction,
  TableEditSource,
  TableEntrySource,
  TableFilterMatch,
} from '@/analytics/enums/table.enums'
import type { TrackEventFn } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent, type AnalyticsEventProperties } from '@/analytics/schema/events.schema'
import type { TableEdit } from '@/tables/schemas'

type TableAnalyticsEvent =
  | AnalyticsEvent.TableCreated
  | AnalyticsEvent.TableOpened
  | AnalyticsEvent.TableRenamed
  | AnalyticsEvent.TableDeleted
  | AnalyticsEvent.TableEdited
  | AnalyticsEvent.TableDocumentsAdded
  | AnalyticsEvent.TableFiltersApplied
  | AnalyticsEvent.TableCopyRequested
  | AnalyticsEvent.TableDownloaded
  | AnalyticsEvent.TableColumnHidden
  | AnalyticsEvent.TableColumnsClicked
  | AnalyticsEvent.TableSortClicked
  | AnalyticsEvent.TableAssistantOpened
  | AnalyticsEvent.TableAssistantClosed
  | AnalyticsEvent.TableReferenceClicked

const reportedAssistantTableIds = new Set<string>()

// Document ids submitted when a table is created, keyed by the upload that creation hands off.
// A later attachment of one of those ids is not a new TableDocumentsAdded. The marker is not read
// by upload or creation behavior.
const creationDocumentIdsByUpload = new Map<string, ReadonlySet<string>>()

function creationUploadKey(tableId: string, clientTmpId: string) {
  return `${tableId}\0${clientTmpId}`
}

export function rememberCreationDocumentIds(tableId: string, clientTmpIds: readonly string[], documentIds: readonly string[]) {
  const counted = new Set(documentIds)
  for (const clientTmpId of clientTmpIds) creationDocumentIdsByUpload.set(creationUploadKey(tableId, clientTmpId), counted)
}

/** True when this upload's document was already included in the creation count. The marker is then dropped. */
export function consumeCreationDocumentCount(tableId: string, clientTmpId: string, documentId: string) {
  const counted = creationDocumentIdsByUpload.get(creationUploadKey(tableId, clientTmpId))
  if (!counted) return false
  creationDocumentIdsByUpload.delete(creationUploadKey(tableId, clientTmpId))
  return counted.has(documentId)
}

export function clearCreationDocumentIds() {
  creationDocumentIdsByUpload.clear()
}

const tableEditActionByRequest: Record<Exclude<TableEdit['action'], 'rename-table'>, TableEditAction> = {
  'add-row': TableEditAction.AddRow,
  'delete-row': TableEditAction.DeleteRow,
  'delete-rows': TableEditAction.DeleteRows,
  'add-column': TableEditAction.AddColumn,
  'add-analysis-column': TableEditAction.AddAnalysisColumn,
  'edit-column': TableEditAction.EditColumn,
  'delete-column': TableEditAction.DeleteColumn,
  'edit-cell': TableEditAction.EditCell,
}

export function clearReportedAssistantTables() {
  reportedAssistantTableIds.clear()
}

function trackTableEvent<T extends TableAnalyticsEvent>(track: TrackEventFn, event: T, props: Omit<AnalyticsEventProperties[T], 'feature'>) {
  track(event, { ...props, feature: kAnalyticsFeatureTables } as AnalyticsEventProperties[T], { suppressFeatureInheritance: true })
}

export function trackTableCreated(
  track: TrackEventFn,
  input: { tableId: string; matterId: string; tableCreationMethod: TableCreationMethod; documentCount?: number }
) {
  trackTableEvent(track, AnalyticsEvent.TableCreated, {
    tableId: input.tableId,
    matterId: input.matterId,
    tableCreationMethod: input.tableCreationMethod,
    ...(input.documentCount === undefined ? {} : { documentCount: input.documentCount }),
  })
}

export function trackAssistantTableCreated(track: TrackEventFn, tableId: string, matterId: string | undefined) {
  if (!matterId || reportedAssistantTableIds.has(tableId)) return
  reportedAssistantTableIds.add(tableId)
  trackTableCreated(track, { tableId, matterId, tableCreationMethod: TableCreationMethod.Assistant })
}

export function trackTableOpened(
  track: TrackEventFn,
  input: { tableId: string; matterId: string; tableEntrySource: TableEntrySource; documentAnalysis?: boolean }
) {
  trackTableEvent(track, AnalyticsEvent.TableOpened, {
    tableId: input.tableId,
    matterId: input.matterId,
    tableEntrySource: input.tableEntrySource,
    ...(input.documentAnalysis === undefined ? {} : { documentAnalysis: input.documentAnalysis }),
  })
}

export function trackTableRenamed(track: TrackEventFn, tableId: string) {
  trackTableEvent(track, AnalyticsEvent.TableRenamed, { tableId })
}

export function trackTableDeleted(track: TrackEventFn, tableId: string) {
  trackTableEvent(track, AnalyticsEvent.TableDeleted, { tableId })
}

function savedTableEditAction(request: Exclude<TableEdit, { action: 'rename-table' }>): TableEditAction {
  // A labeled analysis column is saved as add-column with its question. The action name records
  // whether the user typed a label; the analytics value records that the column runs analysis.
  if (request.action === 'add-column' && request.question?.trim() && request.manual_entry !== true) return TableEditAction.AddAnalysisColumn
  return tableEditActionByRequest[request.action]
}

export function trackTableEdited(track: TrackEventFn, tableId: string, request: TableEdit) {
  if (request.action === 'rename-table') return
  trackTableEvent(track, AnalyticsEvent.TableEdited, {
    tableId,
    tableEditAction: savedTableEditAction(request),
    tableEditSource: TableEditSource.Manual,
  })
}

export function trackTableDocumentsAdded(track: TrackEventFn, tableId: string, documentCount: number, tableDocumentSource: TableDocumentSource) {
  trackTableEvent(track, AnalyticsEvent.TableDocumentsAdded, { tableId, documentCount, tableDocumentSource })
}

export function trackTableFiltersApplied(track: TrackEventFn, tableId: string, filterCount: number, filterMatch: string) {
  if (filterMatch !== TableFilterMatch.All && filterMatch !== TableFilterMatch.Any) return
  trackTableEvent(track, AnalyticsEvent.TableFiltersApplied, { tableId, filterCount, filterMatch })
}

export function trackTableDownloaded(track: TrackEventFn, tableId: string, filtered: boolean) {
  trackTableEvent(track, AnalyticsEvent.TableDownloaded, {
    tableId,
    tableDownloadScope: filtered ? TableDownloadScope.Filtered : TableDownloadScope.All,
  })
}

export function trackTableColumnHidden(track: TrackEventFn, tableId: string) {
  trackTableEvent(track, AnalyticsEvent.TableColumnHidden, { tableId })
}

export function trackTableColumnsClicked(track: TrackEventFn, tableId: string) {
  trackTableEvent(track, AnalyticsEvent.TableColumnsClicked, { tableId })
}

export function trackTableSortClicked(track: TrackEventFn, tableId: string) {
  trackTableEvent(track, AnalyticsEvent.TableSortClicked, { tableId })
}

export function trackTableAssistantOpened(track: TrackEventFn, tableId: string) {
  trackTableEvent(track, AnalyticsEvent.TableAssistantOpened, { tableId })
}

export function trackTableAssistantClosed(track: TrackEventFn, tableId: string) {
  trackTableEvent(track, AnalyticsEvent.TableAssistantClosed, { tableId })
}

export function trackTableReferenceClicked(track: TrackEventFn, tableId: string, referenceId: string) {
  trackTableEvent(track, AnalyticsEvent.TableReferenceClicked, { tableId, tableReferenceId: referenceId })
}

export function trackTableCopyRequested(track: TrackEventFn, input: Omit<AnalyticsEventProperties[AnalyticsEvent.TableCopyRequested], 'feature'>) {
  trackTableEvent(track, AnalyticsEvent.TableCopyRequested, input)
}
